import React, { useState, useEffect, useRef } from 'react';
import '../../styles/Sidebar.css';
import dietaryIcons from '../../constants/dietaryIcons';
import stationDescriptions from '../../constants/stationDescriptions';

function UserSidebar({
  stations,
  selectedStations,
  onToggleStation,
  timePeriods,
  selectedTimes,
  onToggleTime,
  excludeAllergens,
  selectedExclude,
  onToggleExclude,
  preferences,
  selectedPreferences,
  onTogglePreference,
  onCollapse,
}) {
  const MOBILE_BREAKPOINT = 768;

  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const wasMobileRef = useRef(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Keep the parent (App.jsx) in sync with the initial collapsed state.
  useEffect(() => {
    if (onCollapse) {
      onCollapse(isCollapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to the viewport crossing the mobile breakpoint (e.g. resizing the
  // window or rotating a device), without overriding a manual toggle made
  // while staying within the same size class.
  useEffect(() => {
    const handleResize = () => {
      const isNowMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      if (isNowMobile !== wasMobileRef.current) {
        wasMobileRef.current = isNowMobile;
        setIsCollapsed(isNowMobile);
        if (onCollapse) {
          onCollapse(isNowMobile);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapse) {
      onCollapse(newCollapsedState);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button 
        className={`sidebar-toggle ${isCollapsed ? 'collapsed' : 'expanded'}`}
        onClick={toggleSidebar}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <span className="toggle-icon">
          {isCollapsed ? '»' : '«'}
        </span>
      </button>

      <aside className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}>
        <div className="sidebar-content">
          {/* Header */}
          <div className="sidebar-header">
            <h2 className="sidebar-title">
              <span className="text">Filters</span>
            </h2>
          </div>

          {/* Stations */}
          <div className="filter-section">
            <h3 className="section-title">
              <span className="text">Stations</span>
            </h3>
            <div className="filter-options">
              {stations.map((station) => (
                <label
                  key={station}
                  className="filter-label"
                  title={stationDescriptions[station] || ''}
                >
                  <input
                    type="checkbox"
                    checked={selectedStations.includes(station)}
                    onChange={() => onToggleStation(station)}
                    className="filter-checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{station}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Time Periods */}
          <div className="filter-section">
            <h3 className="section-title">
              <span className="text">Time Periods</span>
            </h3>
            <div className="filter-options">
              {timePeriods.map((time) => (
                <label key={time} className="filter-label">
                  <input
                    type="checkbox"
                    checked={selectedTimes.includes(time)}
                    onChange={() => onToggleTime(time)}
                    className="filter-checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{time}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Exclude Allergens */}
          <div className="filter-section">
            <h3 className="section-title">
              <span className="text">Exclude Allergens</span>
            </h3>
            <div className="filter-options">
              {excludeAllergens.map((allergen) => (
                <label key={allergen} className="filter-label">
                  <input
                    type="checkbox"
                    checked={selectedExclude.includes(allergen)}
                    onChange={() => onToggleExclude(allergen)}
                    className="filter-checkbox filter-checkbox-exclude"
                  />
                  <span className="checkmark checkmark-exclude"></span>
                  <span className="label-text">{allergen}</span>
                  <span className="label-icon"> {dietaryIcons[allergen] || '🏷️'}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Dietary Preferences */}
          <div className="filter-section">
            <h3 className="section-title">
              <span className="text">Dietary Preferences</span>
            </h3>
            <div className="filter-options">
              {preferences.map((pref) => (
                <label key={pref} className="filter-label">
                  <input
                    type="checkbox"
                    checked={selectedPreferences.includes(pref)}
                    onChange={() => onTogglePreference(pref)}
                    className="filter-checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{pref}</span>
                  <span className="label-icon">{dietaryIcons[pref] || '🏷️'}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear All Button */}
          <div className="sidebar-footer">
            <button 
              className="clear-button"
              onClick={() => {
                selectedStations.forEach(s => onToggleStation(s));
                selectedTimes.forEach(t => onToggleTime(t));
                selectedExclude.forEach(e => onToggleExclude(e));
                selectedPreferences.forEach(p => onTogglePreference(p));
              }}
              disabled={
                selectedStations.length === 0 && 
                selectedTimes.length === 0 && 
                selectedExclude.length === 0 && 
                selectedPreferences.length === 0
              }
            >
              <span className="text">Clear All Filters</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default UserSidebar;